import isViewModelBuilderValid from './isViewModelBuilderValid';
import extractComponentsFromEnhancedMarkdown from './extractComponentsFromEnhancedMarkdown';
import extractHeadingComponentsFromEnhancedMarkdown from './extractHeadingComponentsFromEnhancedMarkdown';
import fetchJsonFromRpgLogsApi from './fetchJsonFromRpgLogsApi';

async function buildComponentViewModelsFromEnhancedMarkdowns(
  markdowns,
  context,
) {
  const markdown = markdowns.join('\n\n');
  const components = extractComponentsFromEnhancedMarkdown(markdown, context);
  const headingComponents = extractHeadingComponentsFromEnhancedMarkdown(
    markdown,
    context,
  );

  const keysAndBuilders = components
    .map((component) => {
      const viewModelBuilder = component?.type?.viewModelBuilder;

      if (!isViewModelBuilderValid(viewModelBuilder)) {
        return null;
      }

      let nonUndefinedProps = Object.fromEntries(
        Object.entries(component.props).filter(
          ([, value]) => value !== undefined,
        ),
      );

      return {
        key: viewModelBuilder.buildKey(nonUndefinedProps),
        prebuild: async () =>
          // Wrapping in Promise.resolve means prebuild can be defined as either sync or async
          await Promise.resolve(
            viewModelBuilder.prebuild?.(nonUndefinedProps, {
              ...context,
              components,
              headingComponents,
            }),
          ),
        builder: async () =>
          // Wrapping in Promise.resolve means buildModel can be defined as either sync or async
          await Promise.resolve(
            viewModelBuilder.buildModel(nonUndefinedProps, {
              ...context,
              components,
              headingComponents,
            }),
          ),
      };
    })
    .filter((x) => x !== null);

  const uniqueKeysAndBuilders = keysAndBuilders.filter(
    (x, index, array) => array.findIndex((y) => x.key === y.key) === index,
  );

  context.graphQLViewModelQueries = [];
  await Promise.all(
    uniqueKeysAndBuilders.map(
      async (keyAndBuilder) => await keyAndBuilder.prebuild(),
    ),
  );
  if (context.graphQLViewModelQueries.length) {
    const query = `
{
  viewModels {
    ${context.graphQLViewModelQueries
      .map((query) => `${query.key}: ${query.query}`)
      .join('\n')}
  }
}
`;
    const data = await fetchJsonFromRpgLogsApi({ query });

    context.graphQLViewModels = data.viewModels;
  }

  const componentViewModels = {};
  await Promise.all(
    uniqueKeysAndBuilders.map(async (keyAndBuilder) => {
      componentViewModels[keyAndBuilder.key] = await keyAndBuilder.builder();
    }),
  );

  return componentViewModels;
}

export default buildComponentViewModelsFromEnhancedMarkdowns;
